import { type ButtonHTMLAttributes, type ReactNode, useState } from "react";
import { toast } from "@/hooks/use-toast";
import {
  downloadAuthenticatedFile,
  isSameOriginApiPath,
  viewAuthenticatedFile,
} from "@/lib/authenticated-file";
import { getReadableErrorMessage } from "@/lib/queryClient";
import { cn } from "@/lib/utils";

function reportFileError(title: string, err: unknown): void {
  toast({
    title,
    description: getReadableErrorMessage(err),
    variant: "destructive",
  });
}

export function downloadAuthenticatedFileWithToast(url: string, fallbackName?: string): void {
  void downloadAuthenticatedFile(url, fallbackName).catch((err) => {
    reportFileError("Download failed", err);
  });
}

export function viewAuthenticatedFileWithToast(url: string, fallbackName?: string): void {
  void viewAuthenticatedFile(url, fallbackName).catch((err) => {
    reportFileError("Could not open file", err);
  });
}

type AuthenticatedFileLinkProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "type"> & {
  href: string;
  mode?: "download" | "view";
  fallbackName?: string;
  children: ReactNode;
  "data-testid"?: string;
};

/**
 * Document download/view control. Same-origin API paths are fetched with
 * the session (cookies + refresh) and saved from a blob, so a new tab is
 * never opened against a route that would 401. Other URLs stay normal links.
 */
export function AuthenticatedFileLink({
  href,
  mode = "download",
  fallbackName,
  className,
  disabled,
  children,
  "data-testid": testId,
  ...rest
}: AuthenticatedFileLinkProps) {
  const [busy, setBusy] = useState(false);
  if (!isSameOriginApiPath(href)) {
    return (
      <a
        href={href}
        className={className}
        download={mode === "download" ? true : undefined}
        target={mode === "view" ? "_blank" : undefined}
        rel="noopener noreferrer"
        data-testid={testId}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      {...rest}
      data-testid={testId}
      className={cn(
        "inline-flex items-center bg-transparent p-0 border-0 font-inherit cursor-pointer disabled:opacity-60",
        className,
      )}
      disabled={disabled || busy}
      onClick={() => {
        if (busy) return;
        setBusy(true);
        const run = mode === "view"
          ? viewAuthenticatedFile(href, fallbackName)
          : downloadAuthenticatedFile(href, fallbackName);
        void run
          .catch((err) => {
            reportFileError(mode === "view" ? "Could not open file" : "Download failed", err);
          })
          .finally(() => setBusy(false));
      }}
    >
      {children}
    </button>
  );
}
